import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  ComboBox,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  Layer,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioButton,
  RadioButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableExpandHeader,
  TableExpandRow,
  TableExpandedRow,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';

import { SurveyAreaAutocomplete } from 'components/survey-area-autocomplete';
import { FormRow, FormSection } from 'components/forms';
import { FormattedCount } from 'components/formatted-count';
import request from 'lib/request';
import type { ReferenceLocationResponse } from 'types/api-responses';

import { AssetEntityField } from './asset-entity-field';
import {
  createLtdSlotAssetAtom,
  createLtdSlotAssetLoadableAtom,
} from './atoms';
import { LtdMap } from './ltd-map';
import type {
  EntityRef,
  LtdCapableResponse,
  LtdCapableSlot,
  LtdCapableUnit,
} from './types';

import styles from './add-ltd-modal.module.scss';

const INSTRUMENT_TYPE_OPTIONS = [
  'OGL',
  'NPRI',
  'ORRI',
  'OGM',
  'WI',
  'RI',
  'OTHER',
] as const;
type InstrumentType = (typeof INSTRUMENT_TYPE_OPTIONS)[number];

type SearchMode = 'survey-area' | 'unit';
type LtdStep = 1 | 2;

/** Props for {@link AddLtdModal}. */
export interface AddLtdModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Entities the asset may be filed under — the sellers behind the wizard's
   * Step 0 selection. One is filled in; several are offered as a choice; none
   * leaves the Create button disabled with the reason on screen.
   */
  entityOptions: EntityRef[];
  /** Current draft id; when set, the BE attaches via PackageAsset. */
  draftId: string | null;
  /** Fired after a successful POST so the parent can refetch slot views. */
  onSuccess?: () => void;
}

/**
 * Convert FE polygon-array shape (`[[lat, lng], ...]`) — ditto the format
 * the LTD-capable endpoint expects on the wire. SurveyAreaAutocomplete
 * gives a GeoJSON-style geometry; flatten its outer ring into the FE
 * `[lat, lng]` pairs so it round-trips cleanly.
 *
 * @param {GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined | null} geometry - GeoJSON geometry
 * @return {Array<[number, number]> | null} `[lat, lng]` pairs, or null when geometry is missing
 */
function geometryToFePolygon(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined | null
): Array<[number, number]> | null {
  if (!geometry) return null;
  const ring =
    geometry.type === 'Polygon'
      ? geometry.coordinates[0]
      : geometry.coordinates[0]?.[0];
  if (!ring) return null;
  // GeoJSON is [lng, lat]; we send [lat, lng] to match the FE map convention.
  return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
}

/**
 * Two-step modal for creating a Long-Term Development (LTD) slot asset
 * (INS-9040). Step 1 picks a slot via Survey Area or Unit search;
 * Step 2 collects NRI / WI / Property Number and submits.
 *
 * @param {AddLtdModalProps} props - Modal control + target entity / draft
 * @return {JSX.Element} The Create LTD modal
 */
export const AddLtdModal: FC<AddLtdModalProps> = ({
  open,
  onClose,
  entityOptions,
  draftId,
  onSuccess,
}) => {
  const createAsset = useSetAtom(createLtdSlotAssetAtom);
  const createState = useAtomValue(createLtdSlotAssetLoadableAtom);
  const isSubmitting = createState.state === 'loading';

  const [entityId, setEntityId] = useState<number | null>(null);

  // Step 1 — search state
  const [step, setStep] = useState<LtdStep>(1);
  const [searchBy, setSearchBy] = useState<SearchMode>('survey-area');
  const [selectedSurveyArea, setSelectedSurveyArea] =
    useState<ReferenceLocationResponse | null>(null);
  const [unitName, setUnitName] = useState('');
  const [results, setResults] = useState<LtdCapableUnit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Unit-mode only: the unit exists but has no published inventory under the draft's
  // scenario — shown as an info note so the empty table reads as a fact, not a failure.
  const [noInventoryReason, setNoInventoryReason] = useState<string | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [selectedSlot, setSelectedSlot] = useState<{
    slot: LtdCapableSlot;
    unit: LtdCapableUnit;
  } | null>(null);

  // Step 2 — form state
  const [instrumentType, setInstrumentType] = useState<InstrumentType | ''>('');
  const [nri, setNri] = useState('');
  const [wi, setWi] = useState('');
  const [propertyNumber, setPropertyNumber] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const resetForm = useCallback(() => {
    // Close cancels whatever the last search was still doing. Without this the
    // request outlives the modal and writes its results into the state we are
    // clearing right here, so reopening can show rows from the search before.
    abortRef.current?.abort();
    abortRef.current = null;
    setEntityId(null);
    setStep(1);
    setSearchBy('survey-area');
    setSelectedSurveyArea(null);
    setUnitName('');
    setResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setNoInventoryReason(null);
    setExpandedRows({});
    setSelectedSlot(null);
    setInstrumentType('');
    setNri('');
    setWi('');
    setPropertyNumber('');
    setSubmitAttempted(false);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  const runSearch = useCallback(
    async (body: { polygon?: Array<[number, number]>; unit_name?: string }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSearchLoading(true);
      setSearchError(null);
      setNoInventoryReason(null);
      const response = await request.post<
        LtdCapableResponse,
        { error?: string }
      >(
        '/queries/development_areas/ltd_capable',
        // draft_id lets the backend resolve the draft's Type Curve scenario, so the
        // picker lists exactly the slots the evaluation will price.
        { query: { ...body, draft_id: draftId ?? undefined } },
        controller
      );
      if (response.type === 'success') {
        setResults(response.data.results.filter((u) => u.ltdCapable));
        // A unit-mode hit with no inventory arrives as one non-capable row carrying the
        // reason; surface it instead of a silently empty table.
        const uncovered = response.data.results.find(
          (u) => !u.ltdCapable && u.noSlotsReason
        );
        setNoInventoryReason(uncovered?.noSlotsReason ?? null);
        setExpandedRows({});
        setSelectedSlot(null);
      } else {
        setResults([]);
        const errorData = 'data' in response ? response.data : undefined;
        setSearchError(errorData?.error ?? 'LTD search failed');
      }
      setSearchLoading(false);
    },
    [draftId]
  );

  const handleSurveyAreaSelect = useCallback(
    ({
      selectedItem,
    }: {
      selectedItem: ReferenceLocationResponse | undefined | null;
    }) => {
      setSelectedSurveyArea(selectedItem ?? null);
      if (!selectedItem) {
        setResults([]);
        return;
      }
      const polygon = geometryToFePolygon(selectedItem.geometry);
      if (polygon) runSearch({ polygon });
    },
    [runSearch]
  );

  const handleUnitSearch = useCallback(() => {
    const trimmed = unitName.trim();
    if (trimmed.length === 0) return;
    runSearch({ unit_name: trimmed });
  }, [unitName, runSearch]);

  const toggleRow = useCallback((unitId: number) => {
    setExpandedRows((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  }, []);

  const handleSlotPick = useCallback(
    (slot: LtdCapableSlot, unit: LtdCapableUnit) => {
      setSelectedSlot({ slot, unit });
    },
    []
  );

  // Step transitions
  const handleNext = useCallback(() => {
    if (selectedSlot) setStep(2);
  }, [selectedSlot]);

  const handleBack = useCallback(() => {
    // Reset Step 2 fields; preserve Step 1 selection state (AC4).
    setInstrumentType('');
    setNri('');
    setWi('');
    setPropertyNumber('');
    setStep(1);
  }, []);

  const nriNum = nri === '' ? null : Number(nri);
  const wiNum = wi === '' ? null : Number(wi);
  const nriValid =
    nriNum !== null && Number.isFinite(nriNum) && nriNum >= 0 && nriNum <= 1;
  const wiValid =
    wiNum !== null && Number.isFinite(wiNum) && wiNum >= 0 && wiNum <= 1;
  // Reading the choice back out of the options rather than holding the entity
  // itself keeps a stale pick from surviving a Step 0 edit that dropped it.
  const entity =
    entityOptions.length === 1
      ? entityOptions[0]
      : (entityOptions.find((option) => option.id === entityId) ?? null);

  const canAdd =
    entity !== null &&
    selectedSlot !== null &&
    instrumentType !== '' &&
    nriValid &&
    wiValid &&
    !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (
      !canAdd ||
      !entity ||
      !selectedSlot ||
      !instrumentType ||
      nriNum === null
    )
      return;
    setSubmitAttempted(true);
    try {
      await createAsset({
        entityId: entity.id,
        draftId: draftId ?? undefined,
        asset: {
          kind: 'ltd_slot',
          slotId: String(selectedSlot.slot.inventorySlotId),
          formation: selectedSlot.slot.formationName,
          instrumentType,
          nri: nriNum,
          wi: wiNum,
          propertyNumber: propertyNumber || null,
        },
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error surfaces via the loadable state below.
    }
  }, [
    canAdd,
    createAsset,
    draftId,
    entity,
    instrumentType,
    nriNum,
    onClose,
    onSuccess,
    propertyNumber,
    selectedSlot,
    wiNum,
  ]);

  // Scoped to this opening of the modal. The create atom is shared and keeps
  // its last error, so without the flag a failed submit's banner is still on
  // screen the next time the modal opens, blaming the new attempt for the old
  // failure.
  const submitErrorMessage =
    submitAttempted && createState.state === 'hasError'
      ? createState.error instanceof Error
        ? createState.error.message
        : 'Failed to create LTD asset'
      : null;

  // Map search-area polygon — taken from the picked Survey Area's geometry
  // (Survey Area mode) or null (Unit mode — no polygon to highlight).
  const mapSearchPolygon = useMemo(
    () => selectedSurveyArea?.geometry ?? null,
    [selectedSurveyArea]
  );
  const mapUnitPolygon = useMemo(() => {
    const geom = selectedSlot?.unit.geometry;
    if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon'))
      return null;
    return geom;
  }, [selectedSlot]);

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader
        title={step === 1 ? 'Create LTD Asset' : 'Create LTD Asset — Details'}
      />
      <ModalBody>
        <div className={styles.entityRow}>
          <AssetEntityField
            id="add-ltd-entity"
            options={entityOptions}
            value={entity}
            onChange={setEntityId}
            disabled={isSubmitting}
          />
        </div>
        {step === 1 ? (
          <Step1Body
            searchBy={searchBy}
            onSearchByChange={(mode) => {
              setSearchBy(mode);
              setSelectedSurveyArea(null);
              setUnitName('');
              setResults([]);
              setExpandedRows({});
              setSelectedSlot(null);
            }}
            selectedSurveyArea={selectedSurveyArea}
            onSurveyAreaSelect={handleSurveyAreaSelect}
            unitName={unitName}
            onUnitNameChange={setUnitName}
            onUnitSearch={handleUnitSearch}
            mapSearchPolygon={mapSearchPolygon}
            mapUnitPolygon={mapUnitPolygon}
            results={results}
            searchLoading={searchLoading}
            searchError={searchError}
            noInventoryReason={noInventoryReason}
            expandedRows={expandedRows}
            onToggleRow={toggleRow}
            selectedSlotId={selectedSlot?.slot.inventorySlotId ?? null}
            onSlotPick={handleSlotPick}
          />
        ) : (
          <Step2Body
            selectedSlot={selectedSlot}
            instrumentType={instrumentType}
            onInstrumentTypeChange={setInstrumentType}
            nri={nri}
            onNriChange={setNri}
            nriValid={nriValid}
            wi={wi}
            onWiChange={setWi}
            wiValid={wiValid}
            propertyNumber={propertyNumber}
            onPropertyNumberChange={setPropertyNumber}
            disabled={isSubmitting}
            errorMessage={submitErrorMessage}
          />
        )}
      </ModalBody>
      <ModalFooter>
        {step === 1 ? (
          <>
            <Button kind="secondary" onClick={onClose}>
              Cancel
            </Button>
            {/*
              Also blocked when there is no entity to pick at all. The entity
              field lives on this step but Create, which needs one, is on the
              next, so a deal that resolves to none otherwise lets you pick a
              slot, fill in the whole details step, and only then meet a Create
              button that can never enable. Having options but not having
              chosen yet is fine — Create is the gate for that, so the choice
              can still be made after seeing the slot.
            */}
            <Button
              kind="primary"
              onClick={handleNext}
              disabled={selectedSlot === null || entityOptions.length === 0}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button kind="secondary" onClick={handleBack}>
              Back
            </Button>
            <Button kind="primary" onClick={handleSubmit} disabled={!canAdd}>
              {isSubmitting ? 'Creating…' : 'Create and Add'}
            </Button>
          </>
        )}
      </ModalFooter>
    </ComposedModal>
  );
};

/** Step 1 sub-component — search controls + map + results table. */
interface Step1Props {
  searchBy: SearchMode;
  onSearchByChange: (mode: SearchMode) => void;
  selectedSurveyArea: ReferenceLocationResponse | null;
  onSurveyAreaSelect: (data: {
    selectedItem: ReferenceLocationResponse | undefined | null;
  }) => void;
  unitName: string;
  onUnitNameChange: (v: string) => void;
  onUnitSearch: () => void;
  mapSearchPolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  mapUnitPolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  results: LtdCapableUnit[];
  searchLoading: boolean;
  searchError: string | null;
  noInventoryReason: string | null;
  expandedRows: Record<number, boolean>;
  onToggleRow: (unitId: number) => void;
  selectedSlotId: number | null;
  onSlotPick: (slot: LtdCapableSlot, unit: LtdCapableUnit) => void;
}

const Step1Body: FC<Step1Props> = ({
  searchBy,
  onSearchByChange,
  selectedSurveyArea,
  onSurveyAreaSelect,
  unitName,
  onUnitNameChange,
  onUnitSearch,
  mapSearchPolygon,
  mapUnitPolygon,
  results,
  searchLoading,
  searchError,
  noInventoryReason,
  expandedRows,
  onToggleRow,
  selectedSlotId,
  onSlotPick,
}) => (
  <FormSection>
    <p className={styles.modalDescription}>
      Create a Long-Term Development (LTD) asset by searching for a survey area
      to see intersecting LTD-capable units, or by picking a unit directly. Only
      units with LTD-capable properties are shown.
    </p>
    <FormRow>
      <RadioButtonGroup
        legendText="Search By"
        name="ltd-search-by"
        orientation="horizontal"
        valueSelected={searchBy}
        onChange={(value) => onSearchByChange(value as SearchMode)}
      >
        <RadioButton
          labelText="Survey Area"
          value="survey-area"
          id="ltd-radio-sa"
        />
        <RadioButton labelText="Unit" value="unit" id="ltd-radio-unit" />
      </RadioButtonGroup>
    </FormRow>
    <FormRow>
      {searchBy === 'survey-area' ? (
        <SurveyAreaAutocomplete
          id="ltd-survey-area"
          location={
            selectedSurveyArea
              ? { value: selectedSurveyArea, isValid: true, isDirty: true }
              : undefined
          }
          onChange={onSurveyAreaSelect}
          light
        />
      ) : (
        <div className={styles.unitSearchRow}>
          <Layer>
            <TextInput
              id="ltd-unit-name"
              labelText="Unit name"
              placeholder="e.g. U-0000058815"
              value={unitName}
              onChange={(e) => onUnitNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onUnitSearch();
                }
              }}
            />
          </Layer>
          <Button kind="tertiary" size="md" onClick={onUnitSearch}>
            Search
          </Button>
        </div>
      )}
    </FormRow>
    <Accordion>
      <AccordionItem title="Map" open>
        <div className={styles.mapWrap}>
          <LtdMap
            searchPolygon={mapSearchPolygon}
            unitPolygon={mapUnitPolygon}
          />
        </div>
      </AccordionItem>
    </Accordion>
    {searchError !== null && (
      <FormRow>
        <InlineNotification
          kind="error"
          title="Search error"
          subtitle={searchError}
          hideCloseButton
          lowContrast
        />
      </FormRow>
    )}
    {noInventoryReason !== null && (
      <FormRow>
        <InlineNotification
          kind="info"
          title="No inventory"
          subtitle={noInventoryReason}
          hideCloseButton
          lowContrast
        />
      </FormRow>
    )}
    <h4 className={styles.resultsHeading}>
      {searchLoading ? (
        <InlineLoading description="Searching…" status="active" />
      ) : (
        `${results.length} LTD-capable Unit${results.length === 1 ? '' : 's'}`
      )}
    </h4>
    <Table size="md">
      <TableHead>
        <TableRow>
          <TableExpandHeader id="ltd-unit-expand" />
          <TableHeader>Unit</TableHeader>
          <TableHeader>Type</TableHeader>
          <TableHeader>Source</TableHeader>
          <TableHeader>Assigned LTDs</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {results.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className={styles.emptyCell}>
              {searchLoading
                ? 'Searching…'
                : 'Pick a survey area or enter a unit name to view available LTD properties.'}
            </TableCell>
          </TableRow>
        ) : (
          results.map((unit) => {
            const isExpanded = !!expandedRows[unit.id];
            return (
              <Fragment key={unit.id}>
                <TableExpandRow
                  isExpanded={isExpanded}
                  onExpand={() => onToggleRow(unit.id)}
                  expandHeader="ltd-unit-expand"
                  aria-label={`Expand row for ${unit.unit}`}
                >
                  <TableCell>{unit.unit}</TableCell>
                  <TableCell>{unit.unitType ?? '—'}</TableCell>
                  <TableCell>{unit.source}</TableCell>
                  <TableCell>
                    <FormattedCount value={unit.assignedLtds} />
                  </TableCell>
                </TableExpandRow>
                {isExpanded && (
                  <TableExpandedRow colSpan={5}>
                    <Table size="sm" className={styles.slotsTable}>
                      <TableHead>
                        <TableRow>
                          <TableHeader style={{ width: '2rem' }} />
                          <TableHeader>Property ID</TableHeader>
                          <TableHeader>Well Status</TableHeader>
                          <TableHeader>UWI</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {unit.slots.map((slot) => (
                          <TableRow key={slot.inventorySlotId}>
                            <TableCell>
                              <RadioButton
                                id={`ltd-slot-radio-${slot.inventorySlotId}`}
                                labelText=""
                                name="ltd-slot-selection"
                                value={String(slot.inventorySlotId)}
                                checked={
                                  selectedSlotId === slot.inventorySlotId
                                }
                                onChange={() => onSlotPick(slot, unit)}
                              />
                            </TableCell>
                            <TableCell>{slot.slotId}</TableCell>
                            <TableCell>{slot.wellStatus ?? '—'}</TableCell>
                            <TableCell>{slot.uwi ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableExpandedRow>
                )}
              </Fragment>
            );
          })
        )}
      </TableBody>
    </Table>
  </FormSection>
);

/** Step 2 sub-component — readonly summary + NRI form. */
interface Step2Props {
  selectedSlot: { slot: LtdCapableSlot; unit: LtdCapableUnit } | null;
  instrumentType: InstrumentType | '';
  onInstrumentTypeChange: (v: InstrumentType | '') => void;
  nri: string;
  onNriChange: (v: string) => void;
  nriValid: boolean;
  wi: string;
  onWiChange: (v: string) => void;
  wiValid: boolean;
  propertyNumber: string;
  onPropertyNumberChange: (v: string) => void;
  disabled: boolean;
  errorMessage: string | null;
}

const Step2Body: FC<Step2Props> = ({
  selectedSlot,
  instrumentType,
  onInstrumentTypeChange,
  nri,
  onNriChange,
  nriValid,
  wi,
  onWiChange,
  wiValid,
  propertyNumber,
  onPropertyNumberChange,
  disabled,
  errorMessage,
}) => (
  <FormSection>
    <FormRow>
      <Layer>
        <TextInput
          id="ltd-step2-wellbore"
          labelText="Wellbore"
          value={selectedSlot?.slot.uwi ?? selectedSlot?.slot.slotId ?? ''}
          disabled
          placeholder="—"
        />
      </Layer>
      <Layer>
        <TextInput
          id="ltd-step2-unit"
          labelText="Unit"
          value={selectedSlot?.unit.unit ?? ''}
          disabled
          placeholder="—"
        />
      </Layer>
    </FormRow>
    <FormRow>
      <Layer>
        <ComboBox<InstrumentType>
          id="ltd-step2-instrument-type"
          titleText="Interest Type"
          placeholder="Choose an option"
          items={[...INSTRUMENT_TYPE_OPTIONS]}
          itemToString={(t) => t || ''}
          selectedItem={instrumentType || null}
          onChange={({ selectedItem }) =>
            onInstrumentTypeChange(selectedItem ?? '')
          }
          disabled={disabled}
        />
      </Layer>
    </FormRow>
    <FormRow>
      <Layer>
        <TextInput
          id="ltd-step2-nri"
          labelText="NRI"
          helperText="0–1, e.g. 0.16667"
          value={nri}
          onChange={(e) => onNriChange(e.target.value)}
          invalid={nri !== '' && !nriValid}
          invalidText="NRI must be between 0 and 1"
          disabled={disabled}
        />
      </Layer>
      <Layer>
        <TextInput
          id="ltd-step2-wi"
          labelText="WI"
          helperText="0–1, e.g. 0.5"
          value={wi}
          onChange={(e) => onWiChange(e.target.value)}
          invalid={wi !== '' && !wiValid}
          invalidText="WI must be between 0 and 1"
          disabled={disabled}
        />
      </Layer>
    </FormRow>
    <FormRow>
      <Layer>
        <TextInput
          id="ltd-step2-property-number"
          labelText="Property Number (optional)"
          value={propertyNumber}
          onChange={(e) => onPropertyNumberChange(e.target.value)}
          disabled={disabled}
        />
      </Layer>
    </FormRow>
    {errorMessage && (
      <FormRow>
        <InlineNotification
          kind="error"
          title="Failed to create LTD asset"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
        />
      </FormRow>
    )}
  </FormSection>
);
